/**
 * /portal/completar-perfil — ruta canónica del form post-pago.
 * Alias legacy `/completar-registro` también renderiza esta misma página.
 *
 * Formulario post-registro: guarda perfil en Clerk `unsafeMetadata` y redirige a
 * `MEMBERSHIP_ROUTE.PENDING_APPROVAL`. Parámetros de URL se propagan
 * (Stripe `session_id`, MP `subscription_id`, plan, source).
 * Guía pública: `MEMBERSHIP_ACCESS_PORTAL_PATH`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/hooks/useSEO";
import {
  getRegistrationData,
  isClerkAdmin,
  isProfileComplete,
  MIN_REGISTRATION_AGE,
  validateRegistrationData,
} from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import {
  buildFreeJoinReadyUrl,
  isFreeJoinProfileRequest,
  persistFreeJoinContext,
} from "@/lib/freeJoinFlow";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import {
  getCompleteRegistrationStrings,
  resolveMembershipFlowLanguage,
} from "@/lib/membershipFlowI18n";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { capturePostPurchaseProfileCompletedPostHog } from "@/lib/posthogAnalytics";
import { cn } from "@/lib/utils";
import { RedirectToSignIn, useAuth, useUser } from "@clerk/clerk-react";
import { Check, Clock, Loader2, Sparkles } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";

function toIsoDate(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function getMaxBirthDate(): string {
  const now = new Date();
  const max = new Date(now.getFullYear() - MIN_REGISTRATION_AGE, now.getMonth(), now.getDate());
  return max.toISOString().slice(0, 10);
}

function buildPendingTarget(sessionId: string | null, sp: URLSearchParams): string {
  const q = new URLSearchParams();
  if (sessionId) q.set("session_id", sessionId);
  const mpSub = sp.get("subscription_id");
  if (mpSub) q.set("subscription_id", mpSub);
  const fromVipFlag =
    sp.get("from_special_offer") === "1" || sp.get("from_vip") === "1";
  // Prefer explicit plan_tier; post-Stripe VIP always has from_vip=1 even if
  // plan_tier was stripped from the return URL.
  const planTier =
    (sp.get("plan_tier") || "").trim() || (fromVipFlag ? "vip" : "");
  if (planTier) q.set("plan_tier", planTier);
  if (sp.get("from_basic") === "1") q.set("from_basic", "1");
  if (sp.get("from_standard") === "1") q.set("from_standard", "1");
  if (fromVipFlag) q.set("from_vip", "1");
  if (sp.get("access_request") === "1") q.set("access_request", "1");
  if (sp.get("from_whatsapp") === "1") q.set("from_whatsapp", "1");
  const qs = q.toString();
  return qs ? `${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?${qs}` : MEMBERSHIP_ROUTE.PENDING_APPROVAL;
}

export default function CompleteRegistration() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const lang = useMemo(
    () => resolveMembershipFlowLanguage(params, pathname),
    [params, pathname],
  );
  const i18n = useMemo(() => getCompleteRegistrationStrings(lang), [lang]);
  useSEO({
    title: i18n.seoTitle,
    description: i18n.seoDescription(BRAND_PERSON_NAME),
    keywords: "",
    ogImage: `${BRAND_SITE_URL}/og.png`,
    canonical: `${BRAND_SITE_URL}${
      lang === "en"
        ? "/portal/complete-profile"
        : MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO
    }`,
    robots: "noindex, nofollow" as const,
  });

  const [alias, setAlias] = useState("");
  const [telegram, setTelegram] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const maxBirthDate = useMemo(() => getMaxBirthDate(), []);

  const fromVip = params.get("from_special_offer") === "1" || params.get("from_vip") === "1";
  // Stripe VIP return always has plan_tier=vip&from_vip=1; infer vip if only from_vip.
  const planTier =
    (params.get("plan_tier") || "").trim().toLowerCase() ||
    (fromVip ? "vip" : "");
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" ||
    (planTier === "standard" && !fromBasic && !fromVip);
  const sessionId = params.get("session_id");
  const mercadopagoSubscriptionId = params.get("subscription_id");
  const provider = (params.get("provider") || "").trim().toLowerCase();
  /** Solicitud de acceso SIN pago (botón "Acceso gratis") — crea membership free pending. */
  const isAccessRequest = params.get("access_request") === "1";
  /**
   * Free-join DualCTA/Mux: perfil completo → revelar link grupo free (no pending VIP).
   * Nunca hijackea post-pago (session_id / subscription_id).
   */
  const freeJoinProfile = isFreeJoinProfileRequest(params, {
    hasPaidSession: Boolean(sessionId || mercadopagoSubscriptionId),
  });
  const pendingTarget = buildPendingTarget(sessionId, params);
  const freeJoinReadyTarget = buildFreeJoinReadyUrl({
    language: lang,
    country: params.get("country"),
  });

  /**
   * Tras Stripe embed/hosted: volver aquí con session_id (+ plan_tier/from_vip).
   * ClerkProvider ya usa pathname+search como fallback; forzamos en RedirectToSignIn
   * para no perder session_id al sign-up/sign-in.
   */
  const clerkReturnUrl = useMemo(() => {
    if (typeof window === "undefined") return pathname;
    return `${window.location.origin}${pathname}${
      params.toString() ? `?${params.toString()}` : ""
    }`;
  }, [pathname, params]);

  const postPayLandedTrackedRef = useRef(false);
  const clerkRequiredTrackedRef = useRef(false);

  // Stripe → Clerk: landing post-pago con session_id
  useEffect(() => {
    if (!sessionId || postPayLandedTrackedRef.current) return;
    if (!sessionId.startsWith("cs_")) return;
    postPayLandedTrackedRef.current = true;
    mirrorFunnelEvent({
      event_name: "post_stripe_return_to_clerk",
      template: "CompleteRegistration",
      template_variant: "stripe_to_clerk",
      flow_name: "membership_email_gate_to_sub",
      landing_path: pathname,
      language: lang,
      props: {
        funnel_name: "membership_email_gate_to_sub",
        funnel_stage: "post_payment",
        provider: provider || "stripe",
        has_stripe_session: true,
        stripe_session_id: sessionId,
        plan_tier: planTier || "vip",
        from_vip: fromVip,
        clerk_signed_in: Boolean(isSignedIn),
        clerk_loaded: isLoaded,
      },
    });
  }, [sessionId, pathname, lang, provider, planTier, fromVip, isSignedIn, isLoaded]);

  // Necesita cuenta Clerk para completar perfil post-Stripe
  useEffect(() => {
    if (!isLoaded || isSignedIn || !sessionId) return;
    if (clerkRequiredTrackedRef.current) return;
    clerkRequiredTrackedRef.current = true;
    mirrorFunnelEvent({
      event_name: "clerk_auth_required_post_stripe",
      template: "CompleteRegistration",
      template_variant: "stripe_to_clerk",
      flow_name: "membership_email_gate_to_sub",
      landing_path: pathname,
      language: lang,
      props: {
        funnel_name: "membership_email_gate_to_sub",
        funnel_stage: "clerk_auth",
        has_stripe_session: true,
        stripe_session_id: sessionId,
        plan_tier: planTier || "vip",
        from_vip: fromVip,
      },
    });
  }, [isLoaded, isSignedIn, sessionId, pathname, lang, planTier, fromVip]);

  // Pre-fill desde unsafeMetadata si ya tiene algo
  useEffect(() => {
    if (!user) return;
    const data = getRegistrationData(user);
    if (!alias && data.alias) setAlias(data.alias);
    if (!telegram && data.telegram) setTelegram(data.telegram);
    if (!birthDate && data.birthDate) {
      const parsed = new Date(data.birthDate);
      if (!Number.isNaN(parsed.getTime())) {
        setBirthDate(parsed.toISOString().slice(0, 10));
      }
    }
  }, [alias, telegram, birthDate, user]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <RedirectToSignIn
        // Preserve full post-Stripe URL (session_id, plan_tier, from_vip, lang)
        // so after Clerk auth the user lands back here to finish profile.
        forceRedirectUrl={clerkReturnUrl}
        signInForceRedirectUrl={clerkReturnUrl}
        signUpForceRedirectUrl={clerkReturnUrl}
        fallbackRedirectUrl={clerkReturnUrl}
        signInFallbackRedirectUrl={clerkReturnUrl}
        signUpFallbackRedirectUrl={clerkReturnUrl}
      />
    );
  }

  // Si ya tiene perfil completo: free-join → link free; resto → pending
  if (user && isProfileComplete(user)) {
    if (isClerkAdmin(user)) return <Navigate to="/admin" replace />;
    if (freeJoinProfile) {
      return <Navigate to={freeJoinReadyTarget} replace />;
    }
    return <Navigate to={pendingTarget} replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    const trimmedAlias = alias.trim();
    const trimmedTelegram = telegram.trim();
    const validationError = validateRegistrationData(
      {
        alias: trimmedAlias,
        birthDate,
        telegram: trimmedTelegram,
      },
      lang,
    );
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!termsAccepted) {
      setError(i18n.termsRequired);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const existingMeta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
      await user.update({
        unsafeMetadata: {
          ...existingMeta,
          alias: trimmedAlias,
          birthDate: toIsoDate(birthDate),
          telegram: trimmedTelegram,
          terms_accepted_at: new Date().toISOString(),
          ...(fromVip ? { from_vip_signup: true } : {}),
          ...(fromBasic ? { from_basic_signup: true } : {}),
          ...(fromStandard ? { from_standard_signup: true } : {}),
          ...(planTier ? { purchase_plan_tier: planTier } : {}),
          ...(freeJoinProfile ? { free_join: true, from_free_join: true } : {}),
          ...(sessionId ? { stripe_session_id: sessionId } : {}),
          ...(mercadopagoSubscriptionId
            ? { mercadopago_subscription_id: mercadopagoSubscriptionId }
            : {}),
        },
      });

      capturePostPurchaseProfileCompletedPostHog({
        plan_tier: planTier || (freeJoinProfile ? "free" : null),
        from_vip: fromVip,
        from_basic: fromBasic,
        from_standard: fromStandard,
        stripe_session_id: sessionId,
        mercadopago_subscription_id: mercadopagoSubscriptionId,
      });
      mirrorFunnelEvent({
        event_name: freeJoinProfile
          ? "free_join_profile_completed"
          : "post_purchase_profile_completed",
        template: "CompleteRegistration",
        template_variant: freeJoinProfile ? "free_join" : "stripe_to_clerk",
        flow_name: freeJoinProfile
          ? "free_join_clerk_profile"
          : "membership_email_gate_to_sub",
        landing_path: pathname,
        language: lang,
        customer_email: user.primaryEmailAddress?.emailAddress,
        props: {
          funnel_name: freeJoinProfile
            ? "free_join_clerk_profile"
            : "membership_email_gate_to_sub",
          funnel_stage: "profile_done",
          plan_tier: planTier || (fromVip ? "vip" : freeJoinProfile ? "free" : undefined),
          from_vip: fromVip,
          from_basic: fromBasic,
          from_standard: fromStandard,
          free_join: freeJoinProfile,
          has_stripe_session: Boolean(sessionId),
          stripe_session_id: sessionId || undefined,
          has_mercadopago_subscription: Boolean(mercadopagoSubscriptionId),
          next_path: freeJoinProfile
            ? freeJoinReadyTarget
            : pendingTarget.split("?")[0],
        },
      });

      // Solicitud free: membresía free en cola admin (CRM) + luego revelar link free.
      if (isAccessRequest && !sessionId && !mercadopagoSubscriptionId) {
        try {
          const token = await getToken();
          if (token) {
            await fetch("/api/membership/free-access-request", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                flow_name: freeJoinProfile
                  ? "free_join_clerk_profile"
                  : "free_access_request",
                landing_path: pathname,
                alias: trimmedAlias,
                country: params.get("country") || undefined,
              }),
              keepalive: true,
            });
          }
        } catch (freeReqErr) {
          console.warn(
            "[CompleteRegistration] free-access-request failed (non-blocking):",
            freeReqErr,
          );
        }
      }

      if (isClerkAdmin(user)) {
        navigate("/admin", { replace: true });
        return;
      }

      // Free-join: revelar link del grupo free. Resto: pending (equipo asigna VIP).
      if (freeJoinProfile) {
        persistFreeJoinContext({
          country: params.get("country"),
          language: lang,
        });
        navigate(freeJoinReadyTarget, { replace: true });
        return;
      }
      navigate(pendingTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.errorSaveProfile);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8 flex items-center justify-center">
      <div className="mx-auto max-w-md w-full">
        <Card className="border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
          <CardHeader className="text-center space-y-3 pb-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-amber-300" />
            </div>
            <CardTitle className="text-xl sm:text-2xl text-zinc-100">{i18n.cardTitle}</CardTitle>
            <CardDescription className="text-sm text-zinc-400">
              {freeJoinProfile
                ? i18n.cardDescriptionFreeJoin
                : fromBasic || fromStandard
                  ? i18n.cardDescriptionBasicOrStandard
                  : i18n.cardDescriptionVip}
            </CardDescription>
            {/* Process strip: Paid → Profile (here) → In review → Access */}
            {!freeJoinProfile && (
              <ol
                className="mt-3 flex items-center justify-between gap-1 rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-2 py-2.5"
                aria-label="process"
              >
                {(
                  [
                    { label: i18n.processStepPaid, state: "done" as const },
                    { label: i18n.processStepProfile, state: "current" as const },
                    { label: i18n.processStepInReview, state: "next" as const },
                    { label: i18n.processStepAccess, state: "upcoming" as const },
                  ] as const
                ).map((step, idx, arr) => (
                  <li key={step.label} className="flex min-w-0 flex-1 items-center">
                    <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
                      <span
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                          step.state === "done" &&
                            "bg-emerald-500/25 text-emerald-300 ring-1 ring-emerald-400/35",
                          step.state === "current" &&
                            "bg-amber-500/30 text-amber-100 ring-2 ring-amber-400/45",
                          (step.state === "next" || step.state === "upcoming") &&
                            "bg-white/5 text-zinc-500 ring-1 ring-white/10",
                        )}
                        aria-current={step.state === "current" ? "step" : undefined}
                      >
                        {step.state === "done" ? (
                          <Check className="h-3 w-3" strokeWidth={2.5} />
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <span
                        className={cn(
                          "max-w-full truncate text-center text-[9px] font-semibold uppercase tracking-wide",
                          step.state === "done" && "text-emerald-300/80",
                          step.state === "current" && "text-amber-200",
                          step.state === "next" && "text-zinc-400",
                          step.state === "upcoming" && "text-zinc-600",
                        )}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < arr.length - 1 && (
                      <div
                        className={cn(
                          "mb-4 h-0.5 w-full max-w-[1.25rem] shrink-0 rounded-full",
                          idx === 0 ? "bg-emerald-500/40" : "bg-white/10",
                        )}
                        aria-hidden
                      />
                    )}
                  </li>
                ))}
              </ol>
            )}
          </CardHeader>

          <CardContent className="pt-2">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="alias" className="text-zinc-200">{i18n.aliasLabel}</Label>
                <Input
                  id="alias"
                  value={alias}
                  onChange={(e) => {
                    setAlias(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={i18n.aliasPlaceholder}
                  minLength={3}
                  maxLength={30}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">{i18n.aliasHelp}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="telegram" className="text-zinc-200">{i18n.telegramLabel}</Label>
                <Input
                  id="telegram"
                  value={telegram}
                  onChange={(e) => {
                    setTelegram(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={i18n.telegramPlaceholder}
                  minLength={3}
                  maxLength={50}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">
                  {freeJoinProfile ? i18n.telegramHelpFreeJoin : i18n.telegramHelp}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="birthDate" className="text-zinc-200">{i18n.birthDateLabel}</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  max={maxBirthDate}
                  onChange={(e) => {
                    setBirthDate(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 h-11"
                />
                <p className="text-xs text-zinc-500">{i18n.birthDateHelp(MIN_REGISTRATION_AGE)}</p>
              </div>

              <div className="flex items-start gap-3 rounded-lg bg-black/20 border border-white/10 px-3 py-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => {
                    setTermsAccepted(checked === true);
                    if (error) setError(null);
                  }}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                />
                <Label htmlFor="terms" className="text-xs text-zinc-400 cursor-pointer leading-relaxed">
                  {i18n.termsAccept}{" "}
                  <a
                    href="/terminos-y-condiciones"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
                  >
                    {i18n.termsLink}
                  </a>
                  {" · "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
                  >
                    {i18n.privacyLink}
                  </a>
                  {i18n.termsAfterLink(MIN_REGISTRATION_AGE)}
                </Label>
              </div>

              {error && (
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-200">
                  {error}
                </div>
              )}

              {(freeJoinProfile || fromVip || fromBasic || fromStandard) && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-100 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span>
                    {freeJoinProfile
                      ? i18n.hintAfterSaveFreeJoin
                      : fromVip
                        ? i18n.hintAfterSaveVip
                        : i18n.hintAfterSaveBasicOrStandard}
                  </span>
                </div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-white text-black font-semibold hover:bg-white/90 gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {i18n.submitSaving}
                  </>
                ) : freeJoinProfile ? (
                  i18n.submitFreeJoin
                ) : (
                  i18n.submitIdle
                )}
              </Button>

              <MembershipPortalHint className="pt-1" />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
